import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, Zap, ArrowRight, Bike, CreditCard, Loader2, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING = {
  b: {
    label: "Permis B",
    items: [
      { name: "Heure d'évaluation (avant inscription)", price: "50 €", lookup_key: "evaluation_1h", cta_label: "Réserver et payer", eval_highlight: true, features: ["1 heure obligatoire avant tout pack", "Bilan personnalisé avec un moniteur", "Recommandations pour votre formation"] },
      { name: "20 leçons + 2 offertes (auto & manuelle)", price: "999 €", lookup_key: "permis_b_20lecons", features: ["Livret", "Simulateur", "Boîte auto & manuelle"] },
      { name: "30 leçons (auto & manuelle)", price: "1 349 €", lookup_key: "permis_b_30lecons", features: ["Livret", "Simulateur", "Plus de pratique"], highlight: true },
      { name: "Conduite accompagnée (AAC)", price: "990 €", lookup_key: "permis_b_aac", features: ["Dès 15 ans", "16h de conduite", "5h sur simulateur", "Heure d'évaluation offerte"] },
      { name: "Passerelle auto → manuelle", price: "Sur devis", lookup_key: null, features: ["Pour titulaires du permis B auto", "Leçons ciblées boîte manuelle"] },
      { name: "Pack 5 leçons à la carte", price: "325 €", lookup_key: "permis_b_pack5", features: ["Paiement flexible"] },
      { name: "Pack 10 leçons à la carte", price: "620 €", lookup_key: "permis_b_pack10", features: ["Paiement flexible"] },
      { name: "13 leçons + 5 offertes (boîte auto)", price: "899 €", lookup_key: "permis_b_auto_13", features: ["Livret", "Simulateur"] },
    ],
    accelere: "Besoin de votre permis rapidement ? Découvrez nos formations accélérées",
  },
  moto: {
    label: "Moto",
    items: [
      { name: "Moto A2 — 20 leçons (hors + circulation)", price: "700 €", lookup_key: "moto_a2_20lecons", features: ["Livret"] },
      { name: "Moto 125cm³", price: "300 €", lookup_key: "moto_125", features: ["Attestation"] },
      { name: "Maxi Scooter A2", price: "700 €", lookup_key: "maxi_scooter_a2", features: ["Livret"] },
    ],
    moto: "Découvrez toutes nos formations moto (A2, 125, accéléré) sur notre page dédiée",
  },
  am: {
    label: "Permis AM",
    items: [
      { name: "Quadricycle (dès 14 ans)", price: "499 €", lookup_key: "am_quadricycle", features: ["Théorie + pratique"] },
      { name: "Cyclo BSR (dès 14 ans)", price: "249 €", lookup_key: "am_cyclo_bsr", features: ["Théorie + pratique + BSR"] },
    ],
  },
  pl: {
    label: "Poids Lourd",
    items: [],
    accelere: "Permis C en formation accélérée (10 jours) — découvrez notre page dédiée",
  },
};

function PriceCard({ item }) {
  const [loading, setLoading] = useState(false);
  const slug = item.name.slice(0, 20).replace(/\s+/g, "-").toLowerCase();

  const handlePay = async () => {
    if (!item.lookup_key || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: item.lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      alert("Erreur de paiement. Merci de réessayer ou de nous appeler au 01 41 31 50 61.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`pricing-item-${slug}`}
      className={`relative bg-white rounded-2xl p-6 md:p-8 border-2 lift flex flex-col ${
        item.highlight ? "border-[#FF8C42] shadow-xl"
        : item.eval_highlight ? "border-[#00a86b] shadow-lg ring-2 ring-[#00a86b]/15"
        : "border-black/5 shadow-sm hover:border-[#00a86b]/40 hover:shadow-lg"
      }`}
    >
      {item.highlight && (
        <div className="absolute -top-3 left-6 bg-[#FF8C42] text-white font-headings font-bold text-xs tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
          Le plus choisi
        </div>
      )}
      {item.eval_highlight && !item.highlight && (
        <div className="absolute -top-3 left-6 bg-[#00a86b] text-white font-headings font-bold text-xs tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
          Étape 1 · Obligatoire
        </div>
      )}
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <h3 className="font-headings font-bold text-lg md:text-xl text-[#1a2332] leading-snug">{item.name}</h3>
          <ul className="mt-4 space-y-2">
            {item.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-[#5b6577]">
                <Check className="w-4 h-4 text-[#00a86b] shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-right">
          <div className={`font-headings font-black text-3xl md:text-4xl tracking-tight ${item.highlight ? "text-[#FF8C42]" : "text-[#00a86b]"}`}>
            {item.price}
          </div>
          <div className="text-xs text-[#5b6577] mt-1">TTC</div>
        </div>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        {item.lookup_key && (
          <button
            type="button"
            onClick={handlePay}
            disabled={loading}
            data-testid={item.eval_highlight ? "reserve-and-pay-evaluation" : `pay-online-${slug}`}
            className={`flex-1 inline-flex items-center justify-center gap-2 font-headings font-bold rounded-xl px-5 py-3 text-sm transition-colors ${
              item.highlight
                ? "bg-[#FF8C42] hover:bg-[#e8763a] text-white shadow-md disabled:opacity-70"
                : "bg-[#00a86b] hover:bg-[#00915d] text-white shadow-sm disabled:opacity-70"
            }`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {loading ? "Redirection…" : (item.cta_label || "Payer en ligne")}
          </button>
        )}
        <a
          href="#contact"
          data-testid={`pricing-cta-${slug}`}
          className={`${item.lookup_key ? "sm:flex-none sm:px-5" : "flex-1"} inline-flex items-center justify-center gap-2 font-headings font-bold rounded-xl px-5 py-3 text-sm transition-colors border-2 border-[#1a2332]/15 text-[#1a2332] hover:border-[#00a86b] hover:text-[#00a86b]`}
        >
          {item.lookup_key ? "Nous contacter" : "Demander un devis"}
        </a>
      </div>
    </div>
  );
}

export default function Tarifs() {
  return (
    <section id="tarifs" className="anchor-offset py-24 md:py-32 bg-white" data-testid="tarifs-section">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-2xl mb-12">
          <div className="text-[#00a86b] font-headings font-bold text-sm uppercase tracking-[0.2em]">Tarifs complets</div>
          <h2 className="mt-3 font-headings font-black text-4xl md:text-5xl text-[#1a2332] tracking-tight">
            Des prix clairs, sans frais cachés.
          </h2>
          <p className="mt-4 text-[#5b6577] text-lg">
            <strong className="text-[#00a86b]">Paiement en plusieurs fois</strong> disponible.
          </p>
        </div>

        <Tabs defaultValue="b" className="w-full" data-testid="pricing-tabs">
          <TabsList className="!bg-[#f8faff] !p-1.5 !h-auto inline-flex flex-wrap gap-1 rounded-2xl shadow-sm border border-black/5">
            {Object.entries(PRICING).map(([k, v]) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`tab-${k}`}
                className="!rounded-xl !px-5 !py-2.5 font-headings font-bold text-sm data-[state=active]:!bg-[#00a86b] data-[state=active]:!text-white data-[state=active]:!shadow-md text-[#1a2332]"
              >
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(PRICING).map(([k, v]) => (
            <TabsContent key={k} value={k} className="mt-10">
              {v.items.length > 0 && (
                <div className="grid md:grid-cols-2 gap-5">
                  {v.items.map((item, idx) => <PriceCard key={idx} item={item} />)}
                </div>
              )}
              {v.accelere && (
                <Link
                  to="/accelere"
                  data-testid={`accelere-link-${k}`}
                  className={`${v.items.length > 0 ? "mt-6" : ""} relative block overflow-hidden bg-gradient-to-br from-[#1a2332] via-[#1a2332] to-[#2a3448] text-white rounded-3xl px-6 md:px-8 py-6 md:py-7 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all group`}
                >
                  <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 90% 30%, #FF8C42 0, transparent 55%)" }} />
                  <div className="relative flex flex-col md:flex-row md:items-center gap-5">
                    <div className="w-14 h-14 rounded-2xl bg-[#FF8C42] flex items-center justify-center shrink-0 shadow-lg shadow-[#FF8C42]/30">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="inline-flex items-center gap-1.5 bg-[#FF8C42]/20 text-[#FF8C42] font-headings font-bold text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full">
                        Formations accélérées
                      </div>
                      <div className="mt-2 font-headings font-black text-xl md:text-2xl leading-tight">
                        Formule accélérée — <span className="text-[#FF8C42]">8 avantages offerts</span>
                      </div>
                      <div className="mt-1.5 text-sm text-white/75">
                        Programme intensif · Évaluation, heure d&apos;examen &amp; frais admin offerts · 5h simulateur · soir &amp; dimanche sans majoration · annulation 24h.
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] text-white font-headings font-bold rounded-xl px-5 py-3 text-sm shadow-md transition-colors shrink-0">
                      Découvrir <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              )}
              {v.moto && (
                <Link
                  to="/moto"
                  data-testid={`moto-link-${k}`}
                  className="mt-5 flex items-center gap-4 bg-white border-2 border-dashed border-[#00a86b]/40 hover:border-[#00a86b] rounded-2xl px-6 py-5 transition-colors group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#00a86b]/10 flex items-center justify-center shrink-0">
                    <Bike className="w-5 h-5 text-[#00a86b]" />
                  </div>
                  <div className="flex-1 font-headings font-bold text-[#1a2332] group-hover:text-[#00a86b] transition-colors">
                    {v.moto}
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#00a86b] shrink-0" />
                </Link>
              )}
              {k === "b" && (
                <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="seo-pages-links">
                  <Link to="/permis-boite-automatique" className="text-center bg-white border border-black/10 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-semibold rounded-xl px-4 py-3 text-sm transition-colors" data-testid="link-boite-auto">
                    Permis boîte automatique →
                  </Link>
                  <Link to="/permis-boite-manuelle" className="text-center bg-white border border-black/10 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-semibold rounded-xl px-4 py-3 text-sm transition-colors" data-testid="link-boite-manuelle">
                    Permis boîte manuelle →
                  </Link>
                  <Link to="/conduite-accompagnee" className="text-center bg-white border border-black/10 hover:border-[#00a86b] hover:text-[#00a86b] font-headings font-semibold rounded-xl px-4 py-3 text-sm transition-colors" data-testid="link-aac">
                    Conduite accompagnée →
                  </Link>
                </div>
              )}
              {v.items.length > 0 && (
                <div className="mt-6 text-center text-sm text-[#5b6577] font-body italic" data-testid="lesson-duration-note">
                  1 leçon = 55 minutes
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-10" data-testid="financing-highlights">
          <div className="flex items-center gap-4 bg-[#00a86b] text-white rounded-2xl px-6 py-5 shadow-lg" data-testid="financing-4x">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center">
              <CreditCard className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="font-headings font-black text-xl">Paiement en 3× ou 4× sans frais</div>
              <div className="text-sm text-white/85">Par carte bancaire ou avec Klarna, en une seule case à cocher au moment du paiement.</div>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-4 bg-[#FF8C42]/10 border border-[#FF8C42]/30 rounded-2xl px-6 py-5" data-testid="hours-surcharge-notice">
            <div className="w-12 h-12 rounded-full bg-[#FF8C42]/20 flex items-center justify-center shrink-0">
              <Clock className="w-6 h-6 text-[#FF8C42]" />
            </div>
            <div className="flex-1">
              <div className="font-headings font-black text-lg text-[#1a2332]">Leçons du soir (20h — 22h) : <span className="text-[#FF8C42]">+5 € par heure</span></div>
              <div className="text-sm text-[#3a4557] mt-1">
                Les leçons de conduite programmées entre 20h et 22h sont majorées de 5 € par heure.
                Pour toute question, <a href="tel:+33141315061" className="font-headings font-bold text-[#FF8C42] hover:underline">contactez notre secrétariat au 01 41 31 50 61</a>.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
